import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import {
  signOut, fetchCurrentUser, fetchAllUsers, inviteUser, deleteUser, updateUserProfile, updatePassword,
  fetchTransactions, createTransaction, updateTransaction, deleteTransaction,
  fetchTasksForTransaction, bulkCreateTasks, updateTask,
  uploadDocument, getDocumentUrl, deleteDocument,
  uploadAvatar, uploadBankingDoc, getBankingDocUrl, deleteBankingDoc, fetchBankingDocs,
  fetchChecklistTemplates, createChecklistTemplate, updateChecklistTemplate, deleteChecklistTemplate,
  fetchBrokerageById, updateBrokerage, uploadBrokerageLogo,
} from "./supabaseApi";
import { LoginScreen, ResetPasswordScreen } from "./AuthScreens";
import AKSApp from "./aks_transactions";
import { resolveBrokerageSlug, fetchBrokerageBySlug } from "./tenant";
import IdleTimeoutGuard from "./IdleTimeout";

function Spinner({ message = "Loading…" }) {
  return (
    <div style={{minHeight:"100vh",background:"#141414",display:"flex",
      flexDirection:"column",alignItems:"center",justifyContent:"center",
      fontFamily:"'DM Sans',sans-serif"}}>
      <div style={{width:48,height:48,border:"3px solid #333",
        borderTop:"3px solid #A51C30",borderRadius:"50%",
        animation:"spin 0.8s linear infinite",marginBottom:16}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{color:"#8A8A8A",fontSize:14}}>{message}</div>
    </div>
  );
}

export default function App() {
  const [authState,    setAuthState]    = useState("loading");
  const [currentUser,  setCurrentUser]  = useState(null);
  const [users,        setUsers]        = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  const [loadingMsg,   setLoadingMsg]   = useState("Loading…");
  const [brokerage,    setBrokerage]    = useState(null); // resolved from subdomain, for login-screen branding
  const [loginError,   setLoginError]   = useState("");

  // loadUserData() is invoked from inside a useEffect with an empty deps
  // array (mount-only), so it must not read `brokerage` state directly —
  // that closure is fixed at mount time and would never see the async
  // fetch below resolve. brokerageRef stays current across renders instead,
  // and brokerageResolutionRef lets loadUserData await the fetch finishing
  // at all, since on a page reload with an existing session, getSession()
  // and fetchBrokerageBySlug() are two independent network calls that can
  // resolve in either order.
  const brokerageRef = useRef(null);
  const brokerageResolutionRef = useRef(Promise.resolve());

  // Resolve which brokerage this subdomain belongs to, once, before anything
  // else — purely for branding/UX. Tenant data isolation is enforced by RLS
  // regardless of what this resolves to.
  useEffect(() => {
    const slug = resolveBrokerageSlug();
    if (!slug) return;
    brokerageResolutionRef.current = fetchBrokerageBySlug(slug).then(b => {
      brokerageRef.current = b;
      setBrokerage(b);
    });
  }, []);

  useEffect(() => {
    // Expose updatePassword globally for ProfilePanel
    window._supabaseUpdatePassword = async (newPassword) => {
      await updatePassword(newPassword);
    };
    return () => { delete window._supabaseUpdatePassword; };
  }, []);

  useEffect(() => {
    const hash   = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    if (hash.includes("type=recovery") || params.get("reset") === "true") {
      setAuthState("password_reset");
    }
    if (hash.includes("access_token") || hash.includes("token_hash") ||
        hash.includes("type=") || hash.includes("error_description")) {
      window.history.replaceState(null, "/", window.location.pathname);
    }

    let hasLoadedOnce = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        hasLoadedOnce = true;
        loadUserData(session.user.id);
      } else {
        setAuthState("unauthenticated");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") { setAuthState("password_reset"); return; }

      if (event === "SIGNED_IN" && session) {
        const h = window.location.hash;
        const isInviteFlow = h.includes("type=invite") || h.includes("type=signup") ||
                             (session.user?.invited_at && !session.user?.last_sign_in_at);
        if (isInviteFlow) { window.history.replaceState(null, "/", "/"); setAuthState("password_reset"); return; }

        // Supabase fires SIGNED_IN on token refresh too (e.g. tab regains focus
        // after switching desktops/apps). Only reload all data on a genuine
        // first-time login — not on background token refreshes — so in-progress
        // form data (like a transaction being typed) is never wiped out.
        if (!hasLoadedOnce) {
          hasLoadedOnce = true;
          loadUserData(session.user.id);
        }
        return;
      }

      // TOKEN_REFRESHED fires silently in the background — ignore it entirely,
      // the session is still valid and the app doesn't need to reload anything.
      if (event === "TOKEN_REFRESHED") return;

      if (event === "SIGNED_OUT") {
        hasLoadedOnce = false;
        setCurrentUser(null); setUsers([]); setTransactions([]); setAuthState("unauthenticated");
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  async function loadUserData(userId) {
    setAuthState("loading");
    try {
      setLoadingMsg("Loading your profile…");
      const user = await fetchCurrentUser(userId);

      // If this subdomain resolved to a specific brokerage, make sure this
      // user actually belongs to it — RLS already prevents any real data
      // leakage regardless, this is purely to avoid the confusing UX of a
      // user landing on the wrong org's subdomain (e.g. a stale bookmark).
      // Wait for the subdomain-resolution fetch itself to finish first —
      // it's a separate network call from the session/profile fetches above
      // and isn't guaranteed to resolve before them.
      await brokerageResolutionRef.current;
      const resolvedBrokerage = brokerageRef.current;
      if (resolvedBrokerage && user.brokerageId !== resolvedBrokerage.id) {
        await signOut();
        setCurrentUser(null); setUsers([]); setTransactions([]);
        setLoginError("Your account belongs to a different organization. Please sign in at your organization's own URL.");
        setAuthState("unauthenticated");
        return;
      }

      // Always (re-)fetch the user's own brokerage by id — not just relying
      // on subdomain resolution — so branding is correct in the main app
      // regardless of which domain was used to log in. Awaited (not
      // fire-and-forget) because we need to check `active` before loading
      // anything else: a paused brokerage's RLS access is already fully cut
      // off server-side (my_brokerage_id() returns NULL for it, per
      // migration 0010), which would otherwise just load every list below
      // as silently empty — this sign-out gives a clear reason instead.
      const userBrokerage = await fetchBrokerageById(user.brokerageId);
      if (userBrokerage && !userBrokerage.active) {
        await signOut();
        setCurrentUser(null); setUsers([]); setTransactions([]);
        setLoginError("Your brokerage's account is currently paused. Contact your administrator.");
        setAuthState("unauthenticated");
        return;
      }
      setBrokerage(userBrokerage);

      setLoadingMsg("Loading team…");
      const allUsers = await fetchAllUsers();

      setLoadingMsg("Loading checklist template…");
      const templates = await fetchChecklistTemplates();

      setLoadingMsg("Loading transactions…");
      const txList = await fetchTransactions();

      // Fetch task metadata (including document counts) for every transaction —
      // this is fast, it's just a DB query with a join, no per-file network calls.
      // Signed URLs (the slow part) are NOT resolved here — only when a
      // checklist is actually opened via dbOps.loadChecklist().
      setLoadingMsg("Loading checklists…");
      const txWithTasks = await Promise.all(
        txList.map(async tx => {
          try {
            const tasks = await fetchTasksForTransaction(tx.id, false); // false = skip signed URLs
            return { ...tx, tasks };
          } catch(e) {
            console.warn("Could not load tasks for tx", tx.id, e.message);
            return { ...tx, tasks: [] };
          }
        })
      );

      // Set all state atomically
      setCurrentUser(user);
      setUsers(allUsers);
      setChecklistTemplates(templates);
      setTransactions(txWithTasks);
      setAuthState("authenticated");
    } catch (err) {
      console.error("Load error:", err);
      setAuthState("authenticated");
    }
  }

  const dbOps = {
    async createTx(tx) {
      // RLS's INSERT policy requires brokerage_id to match the caller's own —
      // stamp it here rather than trusting it to already be set on tx.
      const created = await createTransaction({ ...tx, brokerageId: currentUser.brokerageId });
      if (tx.tasks?.length > 0) {
        const tasks = await bulkCreateTasks(created.id, tx.tasks);
        created.tasks = tasks;
      } else { created.tasks = []; }
      setTransactions(prev => [created, ...prev]);
      return created;
    },
    async updateTx(id, updates) {
      const updated = await updateTransaction(id, updates);
      setTransactions(prev => prev.map(t =>
        t.id === id ? { ...t, ...updated, tasks: t.tasks } : t
      ));
      return updated;
    },
    async saveTasks(txId, tasks) {
      // Separate genuinely new tasks (locally-generated ID, not yet in DB)
      // from existing tasks (real UUID from a prior fetch/insert)
      const isLocalId = id => typeof id === "string" && id.startsWith("task_");
      const newTasks      = tasks.filter(t => isLocalId(t.id));
      const existingTasks = tasks.filter(t => !isLocalId(t.id));

      // Insert brand-new tasks (e.g. from "Add to Checklist" sync, or custom tasks)
      if (newTasks.length > 0) {
        await bulkCreateTasks(txId, newTasks, existingTasks.length);
      }

      // Update existing tasks (completed/dueDate/notes only — documents handled separately)
      if (existingTasks.length > 0) {
        await Promise.all(existingTasks.map(task => updateTask(task.id, {
          completed: task.completed, dueDate: task.dueDate, notes: task.notes,
        })));
      }

      // Reload from DB to get real UUIDs for any newly-inserted tasks
      const refreshedTasks = await fetchTasksForTransaction(txId);
      setTransactions(prev => prev.map(t => t.id === txId ? { ...t, tasks: refreshedTasks } : t));
    },
    async deleteTx(id) {
      // RLS's "principal deletes any tx" policy (or "agent deletes own draft
      // tx") enforces who's actually allowed to do this now.
      await deleteTransaction(id);
      setTransactions(prev => prev.filter(t => t.id !== id));
    },
    async loadChecklist(txId) {
      return fetchTasksForTransaction(txId, true); // resolves signed URLs
    },
    async uploadDoc(taskId, txId, file) {
      const doc = await uploadDocument(taskId, txId, file, currentUser.id, currentUser.name);
      const tasks = await fetchTasksForTransaction(txId, true);
      setTransactions(prev => prev.map(t => t.id === txId ? { ...t, tasks } : t));
      return doc;
    },
    async getDocUrl(storagePath) { return getDocumentUrl(storagePath); },
    async deleteDoc(docId, storagePath, txId) {
      await deleteDocument(docId, storagePath);
      const tasks = await fetchTasksForTransaction(txId, true);
      setTransactions(prev => prev.map(t => t.id === txId ? { ...t, tasks } : t));
    },
    async saveUser(updatedUser) {
      const { id, ...fields } = updatedUser;
      if (fields.photoFile) {
        fields.photo_url = await uploadAvatar(id, fields.photoFile);
        delete fields.photoFile;
      }
      const { updateUser } = await import("./supabaseApi");
      const saved = await updateUser(id, {
        name: fields.name, license_no: fields.licenseNo || fields.license_no,
        phone: fields.phone, address: fields.address, city: fields.city,
        state: fields.state, zip: fields.zip, dob: fields.dob,
        photo_url: fields.photo_url || fields.photoUrl,
        emergency: fields.emergency, fees: fields.fees,
        broker_id: fields.brokerId || fields.broker_id,
      });
      const normalized = { ...updatedUser, ...saved, id };
      setUsers(prev => prev.map(u => u.id === id ? normalized : u));
      if (currentUser.id === id) setCurrentUser(normalized);
      return normalized;
    },
    async inviteUser({ action, userId, name, email, role, brokerId, licenseNo, phone }) {
      if (action === "invite") {
        const newUser = await inviteUser({ name, email, role, brokerId, licenseNo, phone });
        setUsers(prev => [...prev, newUser]);
        return newUser;
      } else {
        const updated = await updateUserProfile(userId, { name, role, brokerId, licenseNo, phone });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updated } : u));
        return updated;
      }
    },
    async deleteUser(userId) {
      await deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    },
    async createChecklistTemplate(tpl) {
      const created = await createChecklistTemplate(currentUser.brokerageId, tpl);
      setChecklistTemplates(prev => [...prev, created].sort((a,b)=>a.sortOrder-b.sortOrder));
      return created;
    },
    async updateChecklistTemplate(id, updates) {
      const updated = await updateChecklistTemplate(id, updates);
      setChecklistTemplates(prev => prev.map(t => t.id===id ? updated : t).sort((a,b)=>a.sortOrder-b.sortOrder));
      return updated;
    },
    async deleteChecklistTemplate(id) {
      await deleteChecklistTemplate(id);
      setChecklistTemplates(prev => prev.filter(t => t.id !== id));
    },
    async updateBrokerage(id, updates) {
      const updated = await updateBrokerage(id, updates);
      setBrokerage(updated);
      return updated;
    },
    async uploadBrokerageLogo(brokerageId, file) {
      const logoUrl = await uploadBrokerageLogo(brokerageId, file);
      const updated = await updateBrokerage(brokerageId, { logoUrl });
      setBrokerage(updated);
      return updated;
    },
    async uploadBankingDoc(userId, docType, file) { return uploadBankingDoc(userId, docType, file); },
    async getBankingDocUrl(path) { return getBankingDocUrl(path); },
    async deleteBankingDoc(userId, docType, path) { return deleteBankingDoc(userId, docType, path); },
    async fetchBankingDocs(userId) { return fetchBankingDocs(userId); },
    async signOut() { await signOut(); },
    reload: () => currentUser && loadUserData(currentUser.id),
  };

  if (authState === "loading")         return <Spinner message={loadingMsg}/>;
  if (authState === "password_reset")  return <ResetPasswordScreen onDone={() => { setAuthState("unauthenticated"); window.history.replaceState(null, "/", "/"); }}/>;
  if (authState === "unauthenticated") return <LoginScreen brokerage={brokerage} initialError={loginError}/>;
  if (!currentUser)                    return <Spinner message="Loading profile…"/>;

  return (
    <>
      <IdleTimeoutGuard idleMinutes={30} warningSeconds={60} onTimeout={() => {
        setLoginError("You were signed out due to inactivity.");
        dbOps.signOut();
      }}/>
      <AKSApp
        supabaseUser={currentUser}
        supabaseUsers={users}
        supabaseTransactions={transactions}
        supabaseChecklistTemplates={checklistTemplates}
        supabaseBrokerage={brokerage}
        dbOps={dbOps}
      />
    </>
  );
}
